import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { StoryTourStep } from '../lib/storyTour';
import { STORY_TOUR_ANCHOR, shouldAdvanceOnArrival } from '../lib/storyTour';
import { STORY_TOUR_COPY, STORY_TOUR_SKIP_LABEL, STORY_TOUR_WELCOME_SECONDARY } from '../lib/storyTourCopy';
import styles from './CoachMark.module.css';

interface CoachMarkProps {
  /** The step to render, or null (render nothing) — resolveStoryTourStep's
   *  output, passed straight through by the mounting host (App.tsx). */
  step: StoryTourStep | null;
  /** The route currently mounted (App.tsx's own location.pathname) — used
   *  ONLY to decide whether this step's anchor can possibly be on-screen
   *  right now; never used to force a navigation itself (mission: advance on
   *  the real action where cheap, never trap the person on a route they
   *  didn't choose). */
  currentPath: string;
  onAdvance: () => void;
  onSkip: () => void;
}

/**
 * The click-coached tour overlay — LANE STORY-TOUR. A SEPARATE, ADDITIVE
 * layer from Onboarding.tsx (the full-screen modal wizard) and
 * QuickStartGuide.tsx (the dismissible checklist Card): this component reads
 * a step from storyTour.ts's pure machine and paints a scrim + a highlighted
 * REAL control (via the `data-tour` attribute already present on that
 * control's own JSX — TabBar's Refine tab, Home's story-score card,
 * VoiceTextarea's mic button, Refine's Cards/résumé toggle) + a step card in
 * Mira's own voice, riding on top of the actual screen rather than replacing
 * it. The compose-not-evolve call (see storyTour.ts's own header) is because
 * neither existing system has an anchoring/highlighting mechanic at all —
 * retrofitting either would mean rebuilding its core rather than adding
 * beside it.
 *
 * MOUNTING: App.tsx renders this once, at the AppShell tier (same containing
 * block — `.shell`, position:relative — as Onboarding's overlay and
 * SideNavDrawer's scrim/drawer), so it can highlight controls on ANY route
 * without being remounted per-page. It renders null whenever `step` is null,
 * so it costs nothing once the tour is inactive.
 *
 * ANCHOR RESOLUTION: `document.querySelector('[data-tour="…"]')` against the
 * CURRENT DOM — genuinely present or genuinely absent, never assumed. A step
 * whose anchor is not currently mounted (e.g. 'first-story' while still on
 * Home) renders the step card WITHOUT a highlight ring, plus an honest
 * "head to Refine to see this" cue in the card itself, rather than either
 * crashing on a null rect or silently pretending a ring is there. This can
 * legitimately happen for a moment even on the right route, too (initial
 * paint before the anchor mounts) — the missing-anchor render is the SAME
 * frame either way, never a special case.
 *
 * SPOTLIGHT TECHNIQUE: a positioned ring element sized/placed to the
 * target's live getBoundingClientRect (recomputed on mount, on every step
 * change, and on scroll/resize — VoiceTextarea's mic button and Home's story
 * card both live inside AppShell's own internally-scrolling `.scrollArea`)
 * with a huge `box-shadow` painted in `--tl-scrim` — the box-shadow itself
 * IS the dimming layer outside the ring, so no separate full-bleed scrim div
 * is needed and no SVG mask/clip-path is required. The ring sits on the SAME
 * `.shell`-relative coordinate space as Onboarding/SideNavDrawer (this
 * component's own root is `position:absolute; inset:0` inside `.shell`), so
 * the rect math subtracts this root's own bounding rect from the target's —
 * correct regardless of the phone-frame's centered-column offset at the
 * ≥1024px breakpoint.
 *
 * ADVANCE: [Next] always works. Additionally, when the step's anchor is a
 * real clickable control (get-to-refine's tab, see-it-land's toggle), a
 * capture-phase click listener on that exact element advances the tour too —
 * "advance on the real action where cheap" — WITHOUT calling
 * preventDefault/stopPropagation, so the real navigation/toggle still fires
 * normally; this only piggybacks an onAdvance() call onto the same click,
 * never gates or delays it.
 *
 * ESCAPE HATCHES: Esc and [Skip tour] always call onSkip(); the tour never
 * blocks navigation (the scrim has no click-catcher over page content — only
 * the ring's dead space and the card itself capture clicks, so the person
 * can always tap through to the real app underneath if they want to ignore
 * the coach mark entirely rather than formally skipping).
 */
export function CoachMark({ step, currentPath, onAdvance, onSkip }: CoachMarkProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [anchorMissing, setAnchorMissing] = useState(false);

  const anchorId = step ? STORY_TOUR_ANCHOR[step] : null;

  useLayoutEffect(() => {
    if (!step || !anchorId) {
      setRect(null);
      setAnchorMissing(false);
      return;
    }

    function measure() {
      const root = rootRef.current;
      const target = document.querySelector(`[data-tour="${anchorId}"]`);
      if (!root || !target) {
        setRect(null);
        setAnchorMissing(true);
        return;
      }
      const rootBox = root.getBoundingClientRect();
      const targetBox = target.getBoundingClientRect();
      setAnchorMissing(false);
      setRect(
        new DOMRect(
          targetBox.left - rootBox.left,
          targetBox.top - rootBox.top,
          targetBox.width,
          targetBox.height,
        ),
      );
    }

    // His 2026-07-20 walk: "needs to show and highlight the spot" — a step
    // whose target sits outside the visible viewport gets scrolled into view
    // ONCE per step activation (instant, not smooth, so the measure below
    // reads the settled position in the same frame; reduced-motion safe by
    // construction). Runs before the first measure, never on subsequent
    // scroll/resize re-measures — the person keeps full scroll control after
    // the reveal.
    const revealTarget = document.querySelector(`[data-tour="${anchorId}"]`);
    if (revealTarget) {
      const box = revealTarget.getBoundingClientRect();
      if (box.top < 0 || box.bottom > window.innerHeight) {
        revealTarget.scrollIntoView({ block: 'center' });
      }
    }

    measure();
    const scrollHost = document.querySelector('[class*="scrollArea"]');
    window.addEventListener('resize', measure);
    scrollHost?.addEventListener('scroll', measure, { passive: true });
    // A short retry covers the one real race this component has: a step
    // whose anchor mounts on the SAME render as the step activates (e.g.
    // navigating into Refine right as 'first-story' begins) may not yet be
    // in the DOM on this effect's first pass. One retry after paint is
    // enough — this is not a polling loop, just a single catch-up measure.
    const retry = window.setTimeout(measure, 150);

    return () => {
      window.removeEventListener('resize', measure);
      scrollHost?.removeEventListener('scroll', measure);
      window.clearTimeout(retry);
    };
  }, [step, anchorId, currentPath]);

  // Piggyback onAdvance() onto a real click on the anchored control itself
  // (mission: "advance on the real action where cheap") — capture phase, no
  // preventDefault/stopPropagation, so the control's own handler (real
  // navigation, real toggle) always still fires untouched.
  useEffect(() => {
    if (!step || !anchorId) return;
    const target = document.querySelector(`[data-tour="${anchorId}"]`);
    if (!target) return;
    function handleRealClick() {
      onAdvance();
    }
    target.addEventListener('click', handleRealClick, { capture: true });
    return () => target.removeEventListener('click', handleRealClick, { capture: true });
  }, [step, anchorId, onAdvance]);

  useEffect(() => {
    if (!step) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onSkip();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [step, onSkip]);

  // His 2026-07-20 walk receipt: the "Let's go to Refine" card was still
  // showing AFTER he'd arrived on Refine (navigated without tapping the
  // ringed tab, so the click-piggyback above never fired). A step that
  // coaches "go there" completes the moment the person is genuinely there —
  // the pure rule lives in storyTour.ts (shouldAdvanceOnArrival), this
  // effect only executes it.
  useEffect(() => {
    if (!step) return;
    if (shouldAdvanceOnArrival(step, currentPath)) onAdvance();
  }, [step, currentPath, onAdvance]);

  if (!step) return null;

  const copy = STORY_TOUR_COPY[step];
  const isWelcome = step === 'welcome';
  const cardPlacement = rect ? resolveCardPlacement(rect) : 'center';
  // His 2026-07-20 walk: the card rides WITH its ring now — just under the
  // target ('below'), or sat on top of it ('above', bottom-half targets like
  // the mic hero / tab bar) — never parked at a fixed viewport offset where
  // it covers the very control it's describing (or hides behind Safari's
  // floating URL bar). Clamped so a target hard against either edge still
  // keeps the card on-screen; 'center' (no ring) keeps its CSS-only position.
  const rootHeight = rootRef.current?.clientHeight ?? window.innerHeight;
  const cardTop =
    rect === null
      ? undefined
      : cardPlacement === 'below'
        ? Math.min(rect.top + rect.height + RING_PADDING + CARD_GAP, rootHeight - CARD_EDGE_MARGIN)
        : Math.max(rect.top - RING_PADDING - CARD_GAP, CARD_EDGE_MARGIN);

  return (
    <div ref={rootRef} className={styles.root} role="dialog" aria-modal="false" aria-label="Meira's tour">
      {rect ? (
        <div
          className={styles.ring}
          style={{
            left: rect.left - RING_PADDING,
            top: rect.top - RING_PADDING,
            width: rect.width + RING_PADDING * 2,
            height: rect.height + RING_PADDING * 2,
          }}
          aria-hidden="true"
        />
      ) : (
        <div className={styles.scrimFull} aria-hidden="true" />
      )}

      <div
        className={`${styles.card} ${styles[`place-${cardPlacement}`]}`}
        style={cardTop !== undefined ? { top: cardTop } : undefined}
        data-tour="story-tour-card"
      >
        <div className={styles.cardHeader}>
          <span className={styles.avatar}>M</span>
          <div className={styles.title}>{copy.title}</div>
        </div>
        <div className={styles.body}>{copy.body}</div>
        {anchorMissing && anchorId ? (
          <div className={styles.missingNote}>
            Head over there and I'll be waiting — or just tap Next.
          </div>
        ) : null}
        <div className={styles.actions}>
          <button type="button" className={styles.primaryButton} onClick={onAdvance}>
            {copy.primaryLabel}
          </button>
          <button type="button" className={styles.secondaryButton} onClick={onSkip}>
            {isWelcome ? STORY_TOUR_WELCOME_SECONDARY : STORY_TOUR_SKIP_LABEL}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Extra breathing room around the highlighted control — large enough that
 *  the ring reads as "this whole tappable area", never a tight crop that
 *  clips the control's own border-radius/shadow. */
const RING_PADDING = 8;

/** Gap between the ring's edge and the card riding with it. */
const CARD_GAP = 14;

/** The card never gets clamped closer than this to the shell's top/bottom
 *  edge — keeps it clear of the tab bar and (on mobile Safari) the floating
 *  URL bar that swallowed the old fixed-offset card in his 7/20 walk. */
const CARD_EDGE_MARGIN = 12;

type CardPlacement = 'center' | 'above' | 'below';

/**
 * Where to render the step card relative to its ring — 'below' when the
 * target sits in the top half of the shell (card has room underneath),
 * 'above' when the target sits in the bottom half (e.g. the tab bar/mic
 * button near the bottom edge — the card would otherwise run off-screen),
 * 'center' when there is no ring at all (welcome/where-to-next). Pure
 * geometry, no window globals beyond the rect already measured, so this
 * could be unit-tested if it ever grows branches beyond a midpoint split —
 * today's one-line rule doesn't yet warrant its own pure module.
 */
function resolveCardPlacement(rect: DOMRect): CardPlacement {
  const midpoint = rect.top + rect.height / 2;
  // A rough shell-height proxy: window.innerHeight covers the mobile-first
  // case (shell IS the viewport); the ≥1024px centered column is shorter
  // than the viewport but the same top/bottom split still holds since the
  // column is vertically centered within it.
  const shellMid = window.innerHeight / 2;
  return midpoint > shellMid ? 'above' : 'below';
}
